import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { boardApi, epicsApi, ticketsApi, type BoardFilters } from "../api/endpoints";
import { ApiError } from "../api/client";
import {
  STATE_LABELS,
  TICKET_STATES,
  TICKET_TYPES,
  TYPE_LABELS,
  type Board,
  type BoardCard,
  type Epic,
  type TicketState,
} from "../api/types";
import { useTeams } from "../hooks/useTeams";
import { TeamPicker } from "../components/TeamPicker";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { timeAgo } from "../utils/time";

export function BoardPage() {
  const { teams, error: teamsError, selectedId, select } = useTeams();
  const navigate = useNavigate();

  const [board, setBoard] = useState<Board | null>(null);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState("");
  const [epicFilter, setEpicFilter] = useState("");
  const [search, setSearch] = useState("");

  const [dragCardId, setDragCardId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<TicketState | null>(null);

  const filters = useMemo<BoardFilters>(
    () => ({ type: typeFilter || undefined, epic: epicFilter || undefined, q: search || undefined }),
    [typeFilter, epicFilter, search],
  );
  const hasFilters = !!(typeFilter || epicFilter || search);

  const epicName = useMemo(() => {
    const map = new Map(epics.map((e) => [e.id, e.title]));
    return (id: string | null) => (id ? map.get(id) ?? "Epic" : null);
  }, [epics]);

  const total = useMemo(
    () => board?.columns.reduce((n, c) => n + c.tickets.length, 0) ?? 0,
    [board],
  );

  const loadBoard = useCallback(async (teamId: string, f: BoardFilters) => {
    setError(null);
    try {
      setBoard(await boardApi.get(teamId, f));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load board.");
    }
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    epicsApi.listByTeam(selectedId).then(setEpics).catch(() => setEpics([]));
    setEpicFilter("");
  }, [selectedId]);

  const debounce = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!selectedId) return;
    setBoard(null);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => void loadBoard(selectedId, filters), 200);
    return () => clearTimeout(debounce.current);
  }, [selectedId, filters, loadBoard]);

  const clearFilters = () => {
    setTypeFilter("");
    setEpicFilter("");
    setSearch("");
  };

  const onDrop = async (targetState: TicketState) => {
    const cardId = dragCardId;
    setDragCardId(null);
    setDragOver(null);
    if (!board || !cardId || !selectedId) return;

    const source = board.columns.find((c) => c.tickets.some((t) => t.id === cardId));
    if (!source || source.state === targetState) return;
    const card = source.tickets.find((t) => t.id === cardId)!;

    const snapshot = board;
    const optimistic: Board = {
      ...board,
      columns: board.columns.map((col) => {
        if (col.state === source.state)
          return { ...col, tickets: col.tickets.filter((t) => t.id !== cardId) };
        if (col.state === targetState)
          return { ...col, tickets: [{ ...card, state: targetState }, ...col.tickets] };
        return col;
      }),
    };
    setBoard(optimistic);
    setMoveError(null);

    try {
      await ticketsApi.changeState(cardId, targetState);
    } catch (err) {
      setBoard(snapshot);
      setMoveError(err instanceof ApiError ? err.message : "Could not move the ticket. Reverted.");
    }
  };

  if (teams === null) return <Loading />;
  if (teamsError) return <ErrorBanner message={teamsError} />;
  if (teams.length === 0)
    return <EmptyState message="No teams yet. Create a team on the Teams page to start a board." />;

  return (
    <section className="page">
      <div className="board-controls">
        <TeamPicker teams={teams} selectedId={selectedId} onChange={select} />
        <button
          className="btn-primary"
          onClick={() => navigate(`/tickets/new?teamId=${selectedId}`)}
        >
          <Icon name="plus" size={18} /> New ticket
        </button>
      </div>

      <div className="card filter-bar">
        <label className="field filter-search grow">
          <span className="field-label">Search</span>
          <div className="filter-search-wrap">
            <Icon name="search" size={17} />
            <input
              placeholder="Search title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </label>
        <label className="field">
          <span className="field-label">Type</span>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {TICKET_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Epic</span>
          <select value={epicFilter} onChange={(e) => setEpicFilter(e.target.value)}>
            <option value="">All epics</option>
            {epics.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title}
              </option>
            ))}
          </select>
        </label>
        {hasFilters && (
          <button className="secondary" onClick={clearFilters}>
            Clear
          </button>
        )}
        <span className="filter-count">
          {total} {total === 1 ? "ticket" : "tickets"}
        </span>
      </div>

      {moveError && <ErrorBanner message={moveError} />}
      {error && <ErrorBanner message={error} />}

      {board === null ? (
        <Loading label="Loading board…" />
      ) : (
        <div className="board-columns">
          {TICKET_STATES.map((state) => {
            const column = board.columns.find((c) => c.state === state);
            const cards = column?.tickets ?? [];
            return (
              <div
                key={state}
                className={dragOver === state ? "board-column drag-over" : "board-column"}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOver !== state) setDragOver(state);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(null);
                }}
                onDrop={() => onDrop(state)}
              >
                <div className="column-header">
                  <span className="column-dot" style={{ background: `var(--st-${state})` }} />
                  <span className="column-title grow">{STATE_LABELS[state]}</span>
                  <span className="count-pill">{cards.length}</span>
                </div>
                <div className="column-cards">
                  {cards.length === 0 ? (
                    <div className="column-empty">Drop tickets here</div>
                  ) : (
                    cards.map((card) => (
                      <Card
                        key={card.id}
                        card={card}
                        epicLabel={epicName(card.epicId)}
                        dragging={dragCardId === card.id}
                        onDragStart={() => setDragCardId(card.id)}
                        onDragEnd={() => {
                          setDragCardId(null);
                          setDragOver(null);
                        }}
                        onOpen={() => navigate(`/tickets/${card.id}`)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Card({
  card,
  epicLabel,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  card: BoardCard;
  epicLabel: string | null;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onOpen: () => void;
}) {
  return (
    <article
      className={dragging ? "ticket-card dragging" : "ticket-card"}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      title="Open ticket"
    >
      <div className="ticket-card-top">
        <span className={`badge badge-${card.type}`}>{TYPE_LABELS[card.type]}</span>
      </div>
      <p className="ticket-card-title">{card.title}</p>
      <div className="ticket-card-foot">
        {epicLabel ? (
          <span className="chip">
            <Icon name="epics" size={12} />
            <span>{epicLabel}</span>
          </span>
        ) : (
          <span />
        )}
        <span className="ticket-card-time">{timeAgo(card.modifiedAt)}</span>
      </div>
    </article>
  );
}
